(() => {
  'use strict';

  const selectors = {
    gridOuter: '[data-products-grid]',
    productLoop: '#CollectionLoop',
    nextLink: '.pagination-custom__next[href]',
    pagination: '.pagination-custom',
    trigger: '[data-collection-load-more-trigger]',
    status: '[data-collection-load-more-status]',
  };

  class CollectionLoadMore extends HTMLElement {
    constructor() {
      super();
      this.isLoading = false;
      this.observer = null;
      this.nextUrl = null;
      this.prefetchedUrl = null;
      this.prefetchedHtml = null;
      this.prefetchPromise = null;
      this.onIntersect = this.onIntersect.bind(this);
    }

    connectedCallback() {
      this.gridOuter = this.closest(selectors.gridOuter);
      this.productLoop = this.gridOuter && this.gridOuter.querySelector(selectors.productLoop);
      this.trigger = this.querySelector(selectors.trigger);
      this.status = this.querySelector(selectors.status);
      this.setNextUrl();

      if (!this.productLoop || !this.trigger || !this.nextUrl || !('IntersectionObserver' in window)) {
        return;
      }

      this.classList.add('collection-lazy-load--enabled');
      this.observer = new IntersectionObserver(this.onIntersect, {
        rootMargin: '2500px 0px',
        threshold: 0,
      });
      this.observer.observe(this.trigger);
      this.schedulePrefetch();
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
    }

    setNextUrl() {
      const nextLink = this.querySelector(selectors.nextLink);
      this.nextUrl = nextLink ? nextLink.href : null;
    }

    schedulePrefetch() {
      if (!this.nextUrl || this.prefetchedUrl === this.nextUrl || this.prefetchPromise) {
        return;
      }

      const prefetch = () => this.prefetchNextPage();

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(prefetch, {timeout: 1500});
      } else {
        window.setTimeout(prefetch, 500);
      }
    }

    prefetchNextPage() {
      if (!this.nextUrl || this.prefetchedUrl === this.nextUrl || this.prefetchPromise) {
        return;
      }

      const url = this.nextUrl;
      this.prefetchPromise = fetch(url)
        .then((response) => response.text())
        .then((html) => {
          this.prefetchedUrl = url;
          this.prefetchedHtml = html;
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.prefetchPromise = null;
        });
    }

    onIntersect(entries) {
      if (!entries.some((entry) => entry.isIntersecting)) {
        return;
      }

      this.loadNextPage();
    }

    loadNextPage() {
      if (this.isLoading || !this.nextUrl) {
        return;
      }

      this.isLoading = true;
      this.gridOuter.classList.add('is-loading');
      this.setStatus('Loading more products...');

      this.getNextPageHtml()
        .then((html) => this.appendNextPage(html))
        .catch((error) => {
          console.error(error);
          this.setStatus('Unable to load more products.');
        })
        .finally(() => {
          this.isLoading = false;
          this.gridOuter.classList.remove('is-loading');
        });
    }

    getNextPageHtml() {
      if (this.prefetchedUrl === this.nextUrl && this.prefetchedHtml) {
        const html = this.prefetchedHtml;
        this.prefetchedUrl = null;
        this.prefetchedHtml = null;
        return Promise.resolve(html);
      }

      if (this.prefetchPromise) {
        return this.prefetchPromise.then(() => {
          if (this.prefetchedUrl === this.nextUrl && this.prefetchedHtml) {
            const html = this.prefetchedHtml;
            this.prefetchedUrl = null;
            this.prefetchedHtml = null;
            return html;
          }

          return fetch(this.nextUrl).then((response) => response.text());
        });
      }

      return fetch(this.nextUrl).then((response) => response.text());
    }

    appendNextPage(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextProductLoop = doc.querySelector(selectors.productLoop);
      const nextLoadMore = doc.querySelector('collection-load-more');

      if (!nextProductLoop) {
        this.setStatus('');
        return;
      }

      const fragment = document.createDocumentFragment();
      const products = Array.from(nextProductLoop.children);
      products.forEach((product) => fragment.appendChild(product));
      this.productLoop.appendChild(fragment);

      if (window.AOS && typeof window.AOS.refreshHard === 'function') {
        window.AOS.refreshHard();
      }

      const nextPagination = nextLoadMore && nextLoadMore.querySelector(selectors.pagination);
      const currentPagination = this.querySelector(selectors.pagination);

      if (nextPagination && currentPagination) {
        currentPagination.replaceWith(nextPagination);
      } else if (currentPagination) {
        currentPagination.remove();
      }

      this.setNextUrl();
      this.setStatus(this.nextUrl ? 'More products loaded.' : 'All products loaded.');

      if (!this.nextUrl && this.observer) {
        this.observer.disconnect();
      } else {
        this.schedulePrefetch();
      }
    }

    setStatus(message) {
      if (this.status) {
        this.status.textContent = message;
      }
    }
  }

  if (!customElements.get('collection-load-more')) {
    customElements.define('collection-load-more', CollectionLoadMore);
  }
})();
